/**
 * Derives this device's video role from the mate roster, fetches a Daily meeting
 * token from the worker, and owns the @merels/video session for the lifetime of the
 * room connection. See ../AGENTS.md for scope and the phase-1 solo-only join note.
 */
import { computed, effect, signal } from '@preact/signals-react'
import { makeVideoSession } from '@merels/video/session'
import type { VideoDeviceState, VideoParticipant, VideoRole, VideoSession } from '@merels/video/session'
import { fetchVideoToken } from '../tokenClient/tokenClient'
import type { VideoDep, VideoDeps } from './video.types'

const disabledLocalState = { cameraOn: false, micOn: false }
const disabledDeviceState: VideoDeviceState = { cameras: [], mics: [], selectedCameraId: null, selectedMicId: null }

export function roleFromMateCount(count: number): VideoRole {
  if (count === 0) return 'viewer'
  if (count === 1) return 'solo'
  return 'shared'
}

function makeDisabledVideoDep(): VideoDep {
  return {
    role: signal<VideoRole>('viewer'),
    participants: signal(new Map<string, VideoParticipant>()),
    localState: signal(disabledLocalState),
    devices: signal(disabledDeviceState),
    isEnabled: signal(false),
    setCameraEnabled: () => {},
    setMicEnabled: () => {},
    setCameraDevice: () => {},
    setMicDevice: () => {},
  }
}

export function makeVideo(deps: VideoDeps): VideoDep {
  const { roomState, mode, clientId, workerUrl } = deps

  // Local-only rooms are colocated (no one is remote) - no Daily session needed.
  // Omni mode does NOT imply this: a networked room may still have other real
  // mates who want video, even while this client plays as all logical players.
  if (mode.isLocalMode()) {
    return makeDisabledVideoDep()
  }

  // Omni mode adds fake mates to this client's roster alongside its own real one
  // rather than replacing it (see localMates.ts) - role must still reflect this
  // client's real presence (normally exactly one real mate, so 'solo'), not the
  // inflated count including Omni mates, which never publish or receive video
  // (see mateListVM.ts's getVideoFields).
  const role = computed<VideoRole>(() => {
    const realLocalMates = roomState.localMates.value.filter((m: any) => !m.isOmniMate)
    return roleFromMateCount(realLocalMates.length)
  })
  const session = signal<VideoSession | null>(null)
  // Derived from the session's own connectionState rather than a manually-set flag, so it
  // correctly drops back to false if the call errors/disconnects after a successful join -
  // not just "did we ever join once".
  const isEnabled = computed(() => session.value?.connectionState.value === 'joined')

  let joinRequested = false
  async function ensureJoined(): Promise<void> {
    const roomId = roomState.roomId.value
    // Phase 1 only actually publishes for the solo role; shared/viewer fall back to
    // the existing thumbnail UI until their audio-only/subscribe-only paths land.
    if (joinRequested || role.value !== 'solo' || !roomId) return
    joinRequested = true
    const { dailyRoomUrl, token } = await fetchVideoToken({ workerUrl, roomId, clientId })
    const newSession = makeVideoSession({ roomUrl: dailyRoomUrl, token, role: role.value, clientId })
    await newSession.join()
    // Role may have left 'solo' while the token fetch/join above was in flight (e.g.
    // entering Omni mode). The effect below re-runs on every role change, but only
    // leaves an existing `session.value` - at that point this one hadn't been
    // assigned yet, so nothing would tear it down without this check.
    if (role.value !== 'solo') {
      void newSession.leave()
      joinRequested = false
      return
    }
    session.value = newSession
  }

  effect(() => {
    const canPublish = roomState.status.value === 'connected' && roomState.videoEnabled.value && role.value === 'solo'
    if (canPublish) {
      void ensureJoined()
    } else if (session.value) {
      // Disconnected, video turned off mid-call, or role left 'solo' (e.g. this client
      // entered Omni mode and now represents more than one mate) - leave immediately.
      // Without reacting to role here, a session already joined as 'solo' keeps
      // publishing after the role changes, since nothing else ever tears it down -
      // which let Omni mode's fake mates inherit this client's still-live camera (see
      // getVideoFields in mateListVM.ts, keyed by clientId, not by which mate is active).
      void session.value.leave()
      session.value = null
      joinRequested = false
    }
  })

  const participants = computed(() => session.value?.participants.value ?? new Map<string, VideoParticipant>())
  const localState = computed(() => session.value?.localState.value ?? disabledLocalState)
  const devices = computed(() => session.value?.devices.value ?? disabledDeviceState)

  return {
    role,
    participants,
    localState,
    devices,
    isEnabled,
    setCameraEnabled: (enabled: boolean) => session.value?.setCameraEnabled(enabled),
    setMicEnabled: (enabled: boolean) => session.value?.setMicEnabled(enabled),
    setCameraDevice: (deviceId: string) => session.value?.setCameraDevice(deviceId),
    setMicDevice: (deviceId: string) => session.value?.setMicDevice(deviceId),
  }
}
